const ALLOWED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/tiff',
  'image/heic',
  'image/heic-sequence',
  'image/heif',
  'image/heif-sequence',
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/markdown',
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/x-matroska',
  'video/x-msvideo',
  'audio/mpeg',
  'audio/wav',
  'audio/webm',
  'audio/mp4',
  'audio/ogg',
  // Office documents — uploadable via the legacy convertHeicFile pass-through
  // before PR #13240. file-type sniffs these by their envelope (CFB for
  // legacy .doc/.xls/.ppt, ZIP for OOXML .docx/.xlsx/.pptx), so we also
  // accept the browser-reported Office MIME via OFFICE_SNIFF_FALLBACKS below.
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
])

interface OfficeSniffFallbacksLookup {
  [key: string]: ReadonlySet<string>
}

// When file-type sniffs a generic container (CFB or ZIP), trust the browser's
// MIME if it identifies a specific Office format backed by that container.
const OFFICE_SNIFF_FALLBACKS: OfficeSniffFallbacksLookup = {
  'application/x-cfb': new Set([
    'application/msword',
    'application/vnd.ms-excel',
    'application/vnd.ms-powerpoint',
  ]),
  'application/zip': new Set([
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ]),
}

interface OfficeExtFallbacksLookup {
  [key: string]: Record<string, string>
}

// When the browser doesn't supply a usable MIME (iOS Safari and some mobile
// browsers omit `file.type` entirely) but file-type sniffs a generic Office
// container, disambiguate via the filename extension so a valid Office
// document isn't silently rejected by the ALLOWED_MIME_TYPES check below.
const OFFICE_EXT_FALLBACKS: OfficeExtFallbacksLookup = {
  'application/x-cfb': {
    doc: 'application/msword',
    xls: 'application/vnd.ms-excel',
    ppt: 'application/vnd.ms-powerpoint',
  },
  'application/zip': {
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  },
}

function getFilenameExt(name: string): string {
  const dotIndex = name.lastIndexOf('.')
  return dotIndex >= 0 ? name.slice(dotIndex + 1).toLowerCase() : ''
}

const HEIC_MIME_TYPES = new Set([
  'image/heic',
  'image/heic-sequence',
  'image/heif',
  'image/heif-sequence',
])

interface MimeToExtLookup {
  [key: string]: string
}

const MIME_TO_EXT: MimeToExtLookup = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/tiff': 'tiff',
  'image/heic': 'heic',
  'image/heic-sequence': 'heic',
  'image/heif': 'heif',
  'image/heif-sequence': 'heif',
  'application/pdf': 'pdf',
  'text/plain': 'txt',
  'text/csv': 'csv',
  'text/markdown': 'md',
}

export class UnsupportedFileTypeError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UnsupportedFileTypeError'
  }
}

export interface DetectedFileType {
  mime: string
  name: string
  isHeic: boolean
}

function rewriteExtension(filename: string, newExt: string): string {
  const dotIndex = filename.lastIndexOf('.')
  const base = dotIndex >= 0 ? filename.slice(0, dotIndex) : filename
  return `${base}.${newExt}`
}

// "%PDF-", the header every PDF opens with.
const PDF_SIGNATURE = [0x25, 0x50, 0x44, 0x46, 0x2d]

// Read through FileReader rather than Blob.arrayBuffer: jsdom, where this is
// unit-tested, implements the former only.
function readBytes(blob: Blob): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.onerror = () =>
      reject(reader.error ?? new Error('Failed to read file'))
    reader.readAsArrayBuffer(blob)
  })
}

// file-type only matches "%PDF-" at offset 0, so it misses PDFs that carry a
// preamble (BOM, stray whitespace, an exotic producer's banner). Scan the head
// slice for the signature anywhere within it instead.
async function containsPdfSignature(head: Blob): Promise<boolean> {
  const bytes = await readBytes(head)
  for (let i = 0; i <= bytes.length - PDF_SIGNATURE.length; i++) {
    if (PDF_SIGNATURE.every((byte, offset) => bytes[i + offset] === byte)) {
      return true
    }
  }
  return false
}

/**
 * Identify a file's MIME type using a layered strategy, in priority order:
 *
 *   1. Magic-byte sniff (`file-type`) — primary signal, ignores filename and
 *      `file.type` because both are user-controllable / browser-flaky.
 *   2. Browser-reported `file.type` corroboration — when the sniff produces a
 *      generic Office container (CFB or OOXML/ZIP), trust the browser to
 *      disambiguate the specific format if it offers one we recognize.
 *   3. Filename extension fallback — for Office containers ONLY, when the
 *      browser MIME is empty (iOS Safari and some mobile browsers omit it),
 *      use the filename extension to pick the variant. This is a deliberate
 *      compromise: bytes alone can't tell .docx from .xlsx from .pptx without
 *      reading ZIP entries, and a CFB/ZIP container with a recognizable
 *      Office extension is a strong enough signal to accept rather than
 *      silently reject a valid upload.
 *   4. Browser-reported `file.type` standalone — only when the byte sniff
 *      returns nothing at all. Markdown (which has no magic bytes) falls back
 *      to its filename extension here, and a `.pdf` whose header sits past
 *      offset 0 is accepted once the head slice is confirmed to carry the PDF
 *      signature.
 *
 * Anything not in `ALLOWED_MIME_TYPES` after this resolution throws.
 *
 * @param file - The browser File object to identify.
 * @returns The resolved MIME, the (possibly extension-corrected) filename,
 *   and an `isHeic` flag for the HEIC-conversion fast path.
 * @throws {UnsupportedFileTypeError} when the file can't be identified or the
 *   resolved MIME isn't in the allow-list.
 */
export async function detectFileType(file: File): Promise<DetectedFileType> {
  // Dynamic import so file-type and its tokenizer deps (~18 KB gzipped) ship in
  // their own chunk loaded on first upload, not the portal's initial bundle.
  const { fileTypeFromBlob } = await import('file-type')
  const slice = file.slice(0, 4096)
  const result = await fileTypeFromBlob(slice)

  let mime: string

  if (result != null) {
    const browserType = file.type
    const officeFallbacks = OFFICE_SNIFF_FALLBACKS[result.mime]
    const officeExtFallbacks = OFFICE_EXT_FALLBACKS[result.mime]
    if (officeFallbacks?.has(browserType)) {
      // file-type identified the container (CFB/ZIP) but the browser knows
      // the specific Office format — trust the browser.
      mime = browserType
    } else if (officeExtFallbacks && browserType === '') {
      // Mobile browsers (notably iOS Safari) sometimes omit file.type. If the
      // filename extension matches a known Office variant of this container,
      // trust the extension rather than rejecting a valid document.
      const extMime = officeExtFallbacks[getFilenameExt(file.name)]
      mime = extMime ?? result.mime
    } else {
      mime = result.mime
    }
  } else {
    const browserType = file.type
    const ext = getFilenameExt(file.name)
    if (ext === 'md' || ext === 'markdown') {
      // Markdown has no magic bytes, so file-type can't sniff it, and browsers
      // report it inconsistently (text/markdown, text/plain, or empty). Trust
      // the extension so a valid .md file isn't silently rejected or mislabeled
      // as plain text.
      mime = 'text/markdown'
    } else if (ext === 'pdf' && (await containsPdfSignature(slice))) {
      // A PDF whose header isn't at offset 0 sniffs as nothing, and browsers
      // that omit file.type leave nothing else to go on. Accept it only when
      // the head slice actually carries the PDF signature, so unknown bytes
      // can't become an application/pdf attachment on the strength of a
      // filename.
      mime = 'application/pdf'
    } else if (ALLOWED_MIME_TYPES.has(browserType)) {
      mime = browserType
    } else {
      throw new UnsupportedFileTypeError(
        `Unsupported file type: "${file.name}" could not be identified. Please upload an image, video, audio, PDF, or plain-text file.`
      )
    }
  }

  if (!ALLOWED_MIME_TYPES.has(mime)) {
    throw new UnsupportedFileTypeError(
      `Unsupported file type: "${file.name}" appears to be "${mime}", which is not allowed. Please upload an image, video, audio, PDF, or plain-text file.`
    )
  }

  const correctExt = MIME_TO_EXT[mime]
  let name = file.name
  if (correctExt != null) {
    const currentExt = getFilenameExt(name)
    // For HEIC variants, any heic/heif extension is acceptable
    const isExtCorrect =
      currentExt === correctExt ||
      (HEIC_MIME_TYPES.has(mime) && /^hei[cf]$/i.test(currentExt))
    if (!isExtCorrect) {
      name = rewriteExtension(name, correctExt)
    }
  }

  return {
    mime,
    name,
    isHeic: HEIC_MIME_TYPES.has(mime),
  }
}
